/** Params a warehouse-scoped route may carry and still be safe to stay on. */
const WAREHOUSE_SCOPED_PARAMS = new Set(["$lang", "$org", "$location"]);

/** Where a switch lands when the current page cannot be kept. */
export const WAREHOUSE_FALLBACK_PATH =
  "/$lang/orgs/$org/internal/whs/$location";

/**
 * Where to land after switching warehouse, given the route currently matched.
 *
 * Switching keeps the page you are on. The warehouse is context, not a
 * destination.
 *
 * The exception is a route identified by something other than the warehouse,
 * such as one task plan. That record belongs to the warehouse being left, so its
 * identifying segments are trimmed until what remains is scoped by warehouse
 * alone - the section index rather than a record that does not exist here.
 *
 * A trimmed path is only used if the route tree knows it, so trimming cannot
 * land the user on a 404. Index routes are registered with a trailing slash, so
 * both forms count as known.
 */
export function warehouseSwitchTarget(
  currentPath: string | undefined,
  isKnownPath: (path: string) => boolean,
): string {
  const identifyingParam = (segment: string) =>
    segment.startsWith("$") && !WAREHOUSE_SCOPED_PARAMS.has(segment);

  if (!currentPath) return WAREHOUSE_FALLBACK_PATH;

  const segments = currentPath.split("/").filter(Boolean);
  while (segments.some(identifyingParam)) {
    segments.pop();
  }

  const trimmed = `/${segments.join("/")}`;
  const known = isKnownPath(trimmed) || isKnownPath(`${trimmed}/`);
  return known ? trimmed : WAREHOUSE_FALLBACK_PATH;
}
